/**
 * Tiny localStorage-backed recent-contacts cache.
 *
 * Keeps the last N recipients the user has resolved by @username,
 * so the Contacts tab can show them instantly without re-resolving.
 * Truncated UAs are display-only — the canonical UA is what we store
 * and what gets passed to the send flow.
 */

const KEY = "telecrypt.recent-contacts.v1";
const MAX_RECENT = 25;

export interface RecentContact {
  /** Username at the time we resolved them. Display only. */
  username: string;
  unifiedAddress: string;
  lastResolvedAt: number;
  /** How many times we've sent to this contact (for sorting later). */
  sendCount: number;
}

function load(): RecentContact[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentContact[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(list: RecentContact[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
  } catch {
    // quota / privacy mode — silent
  }
}

export function recentContacts(): RecentContact[] {
  return load().sort((a, b) => b.lastResolvedAt - a.lastResolvedAt);
}

export function rememberContact(args: {
  username: string;
  unifiedAddress: string;
}): void {
  const list = load();
  const idx = list.findIndex(
    (c) => c.unifiedAddress === args.unifiedAddress,
  );
  if (idx >= 0) {
    const existing = list[idx]!;
    list[idx] = {
      ...existing,
      username: args.username,
      lastResolvedAt: Date.now(),
    };
  } else {
    list.unshift({
      username: args.username,
      unifiedAddress: args.unifiedAddress,
      lastResolvedAt: Date.now(),
      sendCount: 0,
    });
  }
  save(list);
}

export function recordSendTo(unifiedAddress: string): void {
  const list = load();
  const idx = list.findIndex((c) => c.unifiedAddress === unifiedAddress);
  if (idx < 0) return;
  const existing = list[idx]!;
  list[idx] = { ...existing, sendCount: existing.sendCount + 1 };
  save(list);
}

export function forgetContact(unifiedAddress: string): void {
  const list = load().filter((c) => c.unifiedAddress !== unifiedAddress);
  save(list);
}

/**
 * Rename a recent contact. We repurpose the `username` field as the
 * display label — most users won't ever change it, but the few who
 * resolve `@alice` then want to label it "Mom" can do so without us
 * adding a separate column. No-op if the UA isn't in the list.
 */
export function renameContact(unifiedAddress: string, label: string): void {
  const list = load();
  const idx = list.findIndex((c) => c.unifiedAddress === unifiedAddress);
  if (idx < 0) return;
  const trimmed = label.trim();
  if (!trimmed) return;
  const existing = list[idx]!;
  list[idx] = { ...existing, username: trimmed };
  save(list);
}
